import { ChevronDownIcon } from "@heroicons/react/24/outline"
import { ArrowsUpDownIcon } from "@heroicons/react/24/solid"
import { playBracketCardOpenSound } from "@landing/components/landing/LandingSounds"
import { useDisplayMode } from "@landing/providers/DisplayModeProvider"
import type { Outcome } from "@landing/types"
import { AnimatePresence, motion } from "framer-motion"
import { CheckCheck, Clock, ListFilter, Search } from "lucide-react"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import { useSimulatedTrading } from "../../hooks/useSimulatedTrading"
import { BracketCardOutcomeRow } from "./BracketCardOutcomeRow"
import { BracketCardOutcomeStats } from "./BracketCardOutcomeStats"
import { BracketCover } from "./BracketCover"
import { EliminatedDivider } from "./EliminatedDivider"

interface BracketCardProps {
  title: string
  icon?: string
  outcomes: Outcome[]
  onSelectionChange?: (count: number) => void
  imageUrl?: string
  volume?: number
  progress?: number
  ends?: Date
  dateStart?: Date
  dateEnd?: Date
  isExpanded: boolean
  onToggleExpand: () => void
  isFinal?: boolean
  isAnyCardExpanded?: boolean
  heldOutcomes?: string[]
  state: "live" | "upcoming" | "final"
  onPnLChange?: (slug: string, pnl: number) => void
  showOnlyHeldOutcomes?: boolean
  totalExpandedCount?: number
  showEliminatedDivider?: boolean // Controls if eliminated teams divider appears
  eliminatedDividerInteractive?: boolean // Controls if divider is interactive (show/hide) or static label
  showEliminatedByDefault?: boolean // Controls if eliminated outcomes are shown by default (for mybrackets page)
  onCloseOtherCards?: () => void // Auto-close other expanded cards when user shows trading intent
  simulate?: boolean // run fake-market simulation (landing page)
  preSelectedOutcomes?: string[] // Slugs of outcomes to pre-select when component mounts
  onDetailedSelectionChange?: (
    count: number,
    slugs: string[],
    names: string[],
    images: string[],
    colors: string[],
    bracketTitle: string,
  ) => void // Detailed selection callback for landing page
}

const formatPnL = (amount: number): string => {
  const absAmount = Math.abs(amount)
  const sign = amount >= 0 ? "+" : "-"

  if (absAmount >= 1_000_000_000) {
    // For billions, show 2 decimal places, remove trailing zeros
    return `${sign}${Number((absAmount / 1_000_000_000).toFixed(2))}B`
  }
  if (absAmount >= 1_000_000) {
    // For millions, show 2 decimal places, remove trailing zeros
    return `${sign}${Number((absAmount / 1_000_000).toFixed(2))}M`
  }
  if (absAmount >= 1_000) {
    // For thousands, show 1 decimal place, remove trailing zeros
    return `${sign}${Number((absAmount / 1_000).toFixed(1))}K`
  }
  // For values under 1K, show 2 decimal places, remove trailing zeros
  return `${sign}${Number(absAmount.toFixed(2))}`
}

export const BracketCard = React.memo(function BracketCard({
  title,
  icon,
  outcomes: initialOutcomes,
  onSelectionChange,
  onDetailedSelectionChange,
  imageUrl,
  volume = 0,
  progress: initialProgress,
  ends,
  dateStart,
  dateEnd: _dateEnd,
  isExpanded,
  onToggleExpand,
  isFinal = false,
  isAnyCardExpanded = false,
  heldOutcomes = [],
  state,
  onPnLChange,
  showOnlyHeldOutcomes = false,
  totalExpandedCount = 0,
  showEliminatedDivider = true, // Default to true, can be set to false if divider not needed
  eliminatedDividerInteractive = true, // Default to interactive for Discover page
  showEliminatedByDefault = false, // Default to false for Discover page, set to true for My Brackets
  onCloseOtherCards, // Auto-close other expanded cards when user shows trading intent
  simulate = false,
  preSelectedOutcomes = [],
}: BracketCardProps) {
  const [selectedOutcomes, setSelectedOutcomes] = useState<string[]>([])
  const [expandedOutcomes, setExpandedOutcomes] = useState<Set<string>>(new Set())
  const [outcomes, setOutcomes] = useState<Outcome[]>(initialOutcomes)
  const [recentTrades, setRecentTrades] = useState<Record<string, { type: "buy" | "sell"; timestamp: number }>>({})
  const [showLogos, setShowLogos] = useState(false)
  const { displayMode, setDisplayMode } = useDisplayMode()
  const [_isTitleMultiLine, setIsTitleMultiLine] = useState(false)
  const titleRef = useRef<HTMLSpanElement>(null)
  const [_animationStartTime] = useState(() => Date.now())
  const [showFilter, setShowFilter] = useState(false)
  const [filterValue, setFilterValue] = useState("")
  const filterInputRef = useRef<HTMLInputElement>(null)
  const [dynamicPnL, setDynamicPnL] = useState<number | null>(null)
  const [showEliminated, setShowEliminated] = useState(showEliminatedByDefault)

  // Auto-select outcomes passed via preSelectedOutcomes on initial mount
  useEffect(() => {
    if (preSelectedOutcomes.length === 0) return

    setSelectedOutcomes((prev) => {
      if (prev.length > 0) return prev // Do not override if already selected via user interaction
      const names = preSelectedOutcomes
        .map((slug) => initialOutcomes.find((o) => o.slug === slug)?.name)
        .filter((name): name is string => !!name)
      return names
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run only on mount - preSelectedOutcomes and initialOutcomes are intentionally omitted

  // Always show winner at the top for final markets, eliminated at bottom for all markets
  const sortedOutcomes = useMemo(() => {
    return [...outcomes].sort((a, b) => {
      // No manual pinning; Eagles' price dominance handled by simulation hook

      if (isFinal) {
        // For final markets: winners first, then eliminated last
        const aIsWinner = a.state === "won" || Number.parseFloat(a.price) === 100
        const bIsWinner = b.state === "won" || Number.parseFloat(b.price) === 100
        if (aIsWinner && !bIsWinner) return -1
        if (!aIsWinner && bIsWinner) return 1
      }

      // For all markets: eliminated outcomes always go to bottom
      if (a.state === "eliminated" && b.state !== "eliminated") return 1
      if (a.state !== "eliminated" && b.state === "eliminated") return -1

      // For live markets: sort by price (highest first) among non-eliminated
      if (!isFinal && a.state !== "eliminated" && b.state !== "eliminated") {
        return Number.parseFloat(b.price) - Number.parseFloat(a.price)
      }

      return 0
    })
  }, [outcomes, isFinal, simulate])

  // Separate active and eliminated outcomes for rendering
  // Key UX Decisions:
  // 1. Eliminated teams user holds stay in "active" section (portfolio visibility)
  // 2. Only non-held eliminated teams go to "eliminated" section (can be hidden)
  // 3. My Brackets page shows all positions without eliminated divider
  // 4. Eliminated teams always keep dimmed styling regardless of section
  const { activeOutcomes, eliminatedOutcomes } = useMemo(() => {
    let baseOutcomes = sortedOutcomes

    // First filter by held outcomes if in positions mode
    if (showOnlyHeldOutcomes) {
      baseOutcomes = sortedOutcomes.filter((outcome) => heldOutcomes.includes(outcome.slug))
    }

    // Then apply search filter if present
    if (filterValue.trim()) {
      const searchTerm = filterValue.toLowerCase().trim()
      baseOutcomes = baseOutcomes.filter((outcome) => outcome.name.toLowerCase().includes(searchTerm))
    }

    // Separate into active and eliminated sections for UI
    // Business Logic: Clear separation between live opportunities and dead positions
    // Active = teams that can still win, Eliminated = teams that cannot win
    // Held eliminated teams stay in eliminated section but are sorted to top and remain clickable
    const active = baseOutcomes.filter((outcome) => outcome.state !== "eliminated")
    const eliminated = baseOutcomes
      .filter((outcome) => outcome.state === "eliminated")
      .sort((a, b) => {
        // Within eliminated section: held positions first, then non-held
        const aIsHeld = heldOutcomes.includes(a.slug)
        const bIsHeld = heldOutcomes.includes(b.slug)
        if (aIsHeld && !bIsHeld) return -1
        if (!aIsHeld && bIsHeld) return 1
        // Within same category, sort by price (highest first)
        return Number.parseFloat(b.price) - Number.parseFloat(a.price)
      })

    return { activeOutcomes: active, eliminatedOutcomes: eliminated }
  }, [sortedOutcomes, filterValue, showOnlyHeldOutcomes, heldOutcomes])

  // Combine outcomes for rendering based on showEliminated state (unused but kept for future filtering)
  // const filteredOutcomes = useMemo(() => {
  //   return showEliminated ? [...activeOutcomes, ...eliminatedOutcomes] : activeOutcomes
  // }, [activeOutcomes, eliminatedOutcomes, showEliminated])

  // Focus filter input when shown
  useEffect(() => {
    if (showFilter && filterInputRef.current) {
      filterInputRef.current.focus()
    }
  }, [showFilter])

  // Clear filter when card is closed
  useEffect(() => {
    if (!isExpanded) {
      setFilterValue("")
      setShowFilter(false)
    }
  }, [isExpanded])

  // Clear selections and expanded states of eliminated teams when hidden
  useEffect(() => {
    if (!showEliminated) {
      // Only compute eliminated names when needed to avoid array dependency
      const eliminatedTeamNames = outcomes
        .filter((outcome) => outcome.state === "eliminated")
        .map((outcome) => outcome.name)

      // Remove eliminated teams from selections
      setSelectedOutcomes((prev) => prev.filter((name) => !eliminatedTeamNames.includes(name)))

      // Remove eliminated teams from expanded states
      setExpandedOutcomes((prev) => {
        const newSet = new Set(prev)
        for (const name of eliminatedTeamNames) {
          newSet.delete(name)
        }
        return newSet
      })
    }
  }, [showEliminated, outcomes])

  // Calculate progress based on end date
  const [progress] = useState(() => {
    if (initialProgress !== undefined) return initialProgress

    if (ends) {
      const now = Date.now()
      const endTime = ends.getTime()
      const totalDuration = endTime - (dateStart?.getTime() || now)
      const elapsed = now - (dateStart?.getTime() || now)
      const progress = Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100)
      return Math.round(progress)
    }

    return Math.floor(Math.random() * 80) + 10
  })

  // Add check for market end state (unused but kept for future use)
  // const isMarketOver = ends && Date.now() > ends.getTime()

  // Add useEffect for escape key handling
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isExpanded) {
        setSelectedOutcomes([])
        onToggleExpand()
      }
    }

    document.addEventListener("keydown", handleEscapeKey)
    return () => {
      document.removeEventListener("keydown", handleEscapeKey)
    }
  }, [isExpanded, onToggleExpand])

  // Update the parent component when selections change
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(selectedOutcomes.length)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutcomes]) // onSelectionChange is intentionally omitted to prevent infinite loops

  // Trigger detailed selection change callback for landing page
  useEffect(() => {
    if (onDetailedSelectionChange) {
      const slugs = selectedOutcomes
        .map((name) => outcomes.find((o) => o.name === name)?.slug)
        .filter((slug): slug is string => !!slug)
      const images = selectedOutcomes
        .map((name) => outcomes.find((o) => o.name === name)?.imageSrc)
        .filter((src): src is string => !!src)
      const colors = selectedOutcomes
        .map((name) => outcomes.find((o) => o.name === name)?.primaryColor)
        .filter((color): color is string => !!color)
      onDetailedSelectionChange(selectedOutcomes.length, slugs, selectedOutcomes, images, colors, title)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOutcomes, outcomes, title]) // onDetailedSelectionChange is intentionally omitted to prevent infinite loops

  // Simulate real-time P&L fluctuations for live markets with holdings (runs continuously)
  useEffect(() => {
    if (!simulate) return
    if (isFinal || heldOutcomes.length === 0 || state !== "live") return

    const pnlInterval = setInterval(() => {
      setDynamicPnL((_prev) => {
        let basePnL: number
        if (title.includes("NBA")) {
          basePnL = 825500 * (heldOutcomes.length / 10)
        } else if (title.includes("PGA")) {
          const hash = title.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
          basePnL = -((hash % 400000) + 100000) * (heldOutcomes.length / 10)
        } else {
          basePnL =
            ((title.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) % 1000000) + 200000) *
            (heldOutcomes.length / 10)
        }

        // Create P&L fluctuation based on market movement
        const fluctuation = (Math.random() * 100000 - 50000) * (heldOutcomes.length / 10)

        // Dampen the fluctuation to keep trends consistent
        let dampedFluctuation: number
        if (title.includes("NBA")) {
          dampedFluctuation = fluctuation * 0.3 // Keep NBA positive
        } else if (title.includes("PGA")) {
          dampedFluctuation = fluctuation * 0.4 // Keep PGA negative
        } else {
          dampedFluctuation = fluctuation * 0.5 // Keep others positive
        }

        return basePnL + dampedFluctuation
      })
    }, 3000) // Update P&L every 3 seconds

    return () => clearInterval(pnlInterval)
  }, [simulate, isFinal, heldOutcomes.length, title, state])

  // Simulate real-time trading activity when expanded (legacy – disabled when simulate prop is false)
  useEffect(() => {
    if (simulate) return // legacy effect disabled when simulation flag is on because a dedicated hook now handles it
    if (!isExpanded || isFinal) return

    // Track momentum for each outcome
    const momentum = new Map<string, number>()
    let tick = 0

    const interval = setInterval(() => {
      setOutcomes((currentOutcomes) => {
        let newOutcomes = [...currentOutcomes]
        // Reduce number of updates by 50%
        const numToUpdate = Math.floor(Math.random() * 2) + 1
        const indicesToUpdate = new Set<number>()

        while (indicesToUpdate.size < numToUpdate) {
          indicesToUpdate.add(Math.floor(Math.random() * newOutcomes.length))
        }

        let totalChange = 0

        // Reduce news event frequency by 50%
        const newsEvent =
          Math.random() < 0.07
            ? {
                index: Math.floor(Math.random() * newOutcomes.length),
                change: Number((Math.random() * 17.5 - 7.5).toFixed(2)),
              }
            : null

        for (const index of indicesToUpdate) {
          const outcome = newOutcomes[index]
          if (!outcome) continue
          const currentMomentum = momentum.get(outcome.name) || 0

          // Base volatility on seed number (lower seeds are more volatile)
          const seedVolatility = 1 + (9 - Number(outcome.seed)) * 0.35

          // Reduce price change by 50%
          const baseChange = (Math.random() * 7 - 3.5) * seedVolatility
          const momentumInfluence = currentMomentum * 0.45
          let change = baseChange + momentumInfluence

          // Apply news event if this is the chosen outcome
          if (newsEvent && index === newsEvent.index) {
            change += newsEvent.change
          }

          const changeNum = Number.parseFloat(change.toFixed(2))
          const currentPrice = Number.parseFloat(outcome.price)

          if (currentPrice + changeNum < 1) {
            continue
          }

          newOutcomes[index] = {
            ...outcome,
            price: (currentPrice + changeNum).toString(),
            slug: outcome.slug,
            name: outcome.name,
            fanbase: outcome.fanbase,
            acronym: outcome.acronym,
            seed: outcome.seed,
            imageSrc: outcome.imageSrc,
            primaryColor: outcome.primaryColor,
            secondaryColor: outcome.secondaryColor,
            state: outcome.state,
            marketSlug: outcome.marketSlug,
            trend: outcome.trend,
          }
          totalChange += changeNum

          // Update momentum (decay factor of 0.7)
          momentum.set(outcome.name, currentMomentum * 0.7 + changeNum * 0.3)

          if (changeNum !== 0) {
            setRecentTrades((prev) => ({
              ...prev,
              [outcome.name]: {
                type: changeNum > 0 ? "buy" : "sell",
                timestamp: Date.now(),
              },
            }))
          }
        }

        const remainingIndices = Array.from({ length: newOutcomes.length })
          .map((_, i) => i)
          .filter((i) => !indicesToUpdate.has(i))

        let remaining = -totalChange

        while (Math.abs(remaining) > 0.01 && remainingIndices.length > 0) {
          const randomIndex = Math.floor(Math.random() * remainingIndices.length)
          const index = remainingIndices[randomIndex]
          if (index === undefined) break
          const outcome = newOutcomes[index]
          if (!outcome) break
          const currentMomentum = momentum.get(outcome.name) || 0

          // Reduce adjustments by 50%
          const direction = remaining > 0 ? 0.125 : -0.125
          const momentumInfluence = currentMomentum * 0.15
          const adjustedDirection = direction + momentumInfluence

          const currentPrice = Number.parseFloat(outcome.price)

          if (currentPrice + adjustedDirection < 1) {
            remainingIndices.splice(randomIndex, 1)
            continue
          }

          newOutcomes[index] = {
            ...outcome,
            price: (currentPrice + adjustedDirection).toString(),
          }
          remaining -= adjustedDirection

          // Update momentum for remaining outcomes
          momentum.set(outcome.name, currentMomentum * 0.85 + adjustedDirection * 0.15)

          if (Math.random() > 0.7) {
            remainingIndices.splice(randomIndex, 1)
          }
        }

        // Only normalize every 5 ticks to allow for more drift
        tick++
        if (tick % 5 === 0) {
          // Normalize so total is 100 (or close)
          const total = newOutcomes.reduce((sum, o) => sum + Number.parseFloat(o.price), 0)
          if (total > 0) {
            newOutcomes = newOutcomes.map((o) => ({
              ...o,
              price: ((Number.parseFloat(o.price) * 100) / total).toFixed(2),
            }))
          }
        }

        for (const outcome of newOutcomes) {
          outcome.price = Number.parseFloat(outcome.price).toFixed(2)
        }

        return newOutcomes.sort((a, b) => {
          if (a.state === "eliminated" && b.state !== "eliminated") return 1
          if (a.state !== "eliminated" && b.state === "eliminated") return -1
          return Number.parseFloat(b.price) - Number.parseFloat(a.price)
        })
      })
    }, 2000) // Double the interval from 1000ms to 2000ms

    const flashTimeout = setInterval(() => {
      setRecentTrades((prev) => {
        const now = Date.now()
        const updated = { ...prev }
        let hasChanges = false

        for (const [key, value] of Object.entries(updated)) {
          if (now - value.timestamp > 250) {
            delete updated[key]
            hasChanges = true
          }
        }

        // Only return new object if there were actual changes
        return hasChanges ? updated : prev
      })
    }, 100)

    return () => {
      clearInterval(interval)
      clearInterval(flashTimeout)
    }
  }, [simulate, isExpanded, isFinal, heldOutcomes.length, title])

  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => {
        setShowLogos(true)
      }, 300)

      return () => {
        clearTimeout(timer)
      }
    }
    setShowLogos(false)
    return undefined
  }, [isExpanded])

  useEffect(() => {
    const styleElement = document.createElement("style")
    styleElement.textContent = `
      @keyframes topMeterSurge {
        0% { transform: scaleX(1); }
        50% { transform: scaleX(1.02); }
        100% { transform: scaleX(1); }
      }

      @keyframes meterSurge {
        0% { transform: scaleX(1) skew(-16deg); }
        50% { transform: scaleX(1.025) skew(-16deg); }
        100% { transform: scaleX(1) skew(-16deg); }
      }
    `
    document.head.appendChild(styleElement)
    return () => {
      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement)
      }
    }
  }, [])

  useEffect(() => {
    if (titleRef.current) {
      const lineHeight = Number.parseInt(window.getComputedStyle(titleRef.current).lineHeight, 10)
      const height = titleRef.current.clientHeight
      setIsTitleMultiLine(height > lineHeight)
    }
  }, [title])

  const handleOutcomeClick = (outcomeName: string) => {
    const outcome = outcomes.find((o) => o.name === outcomeName)
    if (outcome?.state === "eliminated" && !heldOutcomes.includes(outcome.slug)) {
      toast.error("Eliminated outcome")
      return
    }

    // UX Decision: Auto-close other expanded cards when user shows trading intent
    // This allows portfolio browsing (multiple cards open) but focuses trading to one bracket
    // As soon as user selects an outcome, we close other cards to prevent cross-bracket confusion
    if (totalExpandedCount > 1 && onCloseOtherCards) {
      onCloseOtherCards()
    }

    // Toggle expanded state for this outcome
    setExpandedOutcomes((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(outcomeName)) {
        newSet.delete(outcomeName)
      } else {
        newSet.add(outcomeName)
      }
      return newSet
    })

    setSelectedOutcomes((prev) => {
      if (prev.includes(outcomeName)) {
        // Deselecting: no sound (sound was already played on press)
        return prev.filter((name) => name !== outcomeName)
      }

      if (prev.length >= 5) {
        // Limit reached: error sound was already played on press
        return prev
      }

      // Valid selection: click sound was already played on press
      return [...prev, outcomeName]
    })
  }

  const handleToggleExpand = () => {
    // Play sound only when opening the card
    if (!isExpanded) {
      // Card is currently closed, so play OPEN sound
      playBracketCardOpenSound()
    }

    if (isExpanded && selectedOutcomes.length > 0) {
      setSelectedOutcomes([])
      if (onSelectionChange) {
        onSelectionChange(0)
      }
    }
    onToggleExpand()
  }

  // Add useEffect to clear selections and expanded outcomes when card is closed
  useEffect(() => {
    if (!isExpanded) {
      setSelectedOutcomes([])
      setExpandedOutcomes(new Set())
      if (onSelectionChange) {
        onSelectionChange(0)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]) // onSelectionChange is intentionally omitted to prevent infinite loops

  // Convert percent to price format (unused but kept for future use)
  // const percentToPrice = (percent: string): string => {
  //   const num = Number.parseFloat(percent)
  //   if (num <= 0) return "N/A"
  //   const price = Math.round(num * 10).toString()
  //   return `$${price.length <= 2 ? price : price.padStart(3, "0")}`
  // }

  const toggleDisplayMode = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDisplayMode(displayMode === "chance" ? "price" : "chance")
  }

  const formatVolume = (value: number): string => {
    const absValue = Math.abs(value)
    const sign = value >= 0 ? "" : "-"

    if (absValue >= 1_000_000_000) {
      // For billions, show 2 decimal places, remove trailing zeros
      return `${sign}${Number((absValue / 1_000_000_000).toFixed(2))}B`
    }
    if (absValue >= 1_000_000) {
      // For millions, show 2 decimal places, remove trailing zeros
      return `${sign}${Number((absValue / 1_000_000).toFixed(2))}M`
    }
    if (absValue >= 1_000) {
      // For thousands, show 1 decimal place, remove trailing zeros
      return `${sign}${Number((absValue / 1_000).toFixed(1))}K`
    }
    // For values under 1K, show 2 decimal places, remove trailing zeros
    return `${sign}${Number(absValue.toFixed(2))}`
  }

  // Trade handler (unused but kept for future use)
  // const handleTrade = () => {
  //   // TODO: Implement trade functionality
  //   // Play notification sound in landing demo brackets
  //   if (simulate) {
  //     const clipSrc = brackyNotificationClips[0] // "link" variant only
  //     // Allow this notification sound to overlay any currently playing audio
  //     // so that demo interactions don't interrupt background music or emoji clips.
  //     playSingleAudio(clipSrc, 1, true)
  //   }
  // }

  // Sell handler (unused but kept for future use)
  // const handleSell = () => {
  //   // TODO: Implement sell functionality
  // }

  // Generate a simulated P&L based on the bracket title for consistency
  const simulatedPnL = useMemo(() => {
    if (heldOutcomes.length === 0) return 0

    // Use dynamic P&L if available (when market is live and expanded)
    if (dynamicPnL !== null) return dynamicPnL

    // Make NBA bracket always profitable to simulate user being "up"
    if (title.includes("NBA")) {
      return 825500 * (heldOutcomes.length / 10) // Always positive, similar to screenshot
    }

    // Make PGA bracket show losses for realism
    if (title.includes("PGA")) {
      const hash = title.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
      const baseValue = -((hash % 400000) + 100000) // Range from -100K to -500K
      return baseValue * (heldOutcomes.length / 10)
    }

    // Create a simple hash from the title to get consistent values
    const hash = title.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
    // Bias towards positive values: range from 200K to 1.2M instead of -1M to +1M
    const baseValue = (hash % 1000000) + 200000 // Range from 200K to 1.2M

    // Adjust based on number of holdings
    return baseValue * (heldOutcomes.length / 10)
  }, [title, heldOutcomes.length, dynamicPnL])

  // Generate realistic P&L distribution that sums to total
  const outcomesPnLMap = useMemo(() => {
    const pnlMap = new Map<string, number>()

    if (heldOutcomes.length === 0) return pnlMap

    const totalPnL = simulatedPnL
    const heldOutcomesList = outcomes.filter((outcome) => heldOutcomes.includes(outcome.slug))

    if (heldOutcomesList.length === 0) return pnlMap
    if (heldOutcomesList.length === 1) {
      // Single position gets the full P&L
      const firstOutcome = heldOutcomesList[0]
      if (firstOutcome) {
        pnlMap.set(firstOutcome.slug, totalPnL)
      }
      return pnlMap
    }

    // Generate individual P&L values that are varied (some positive, some negative)
    const individualPnLs: number[] = []
    let runningSum = 0

    // First, generate base P&L values with intentional variation
    for (let i = 0; i < heldOutcomesList.length - 1; i++) {
      const outcome = heldOutcomesList[i]
      if (!outcome) continue

      // Create varied P&L based on outcome characteristics
      const hash = (outcome.name + title + i.toString()).split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
      const variance = (hash % 80000) - 40000 // Range from -40K to +40K for more variation

      // Make some outcomes more likely to be positive/negative based on their characteristics
      const priceInfluence = (Number.parseFloat(outcome.price) - 50) * 800
      const seedInfluence = outcome.seed ? (9 - Number(outcome.seed)) * 1500 : 0

      let individualPnL = variance + priceInfluence + seedInfluence

      // Ensure we have realistic variation - force some positions to be opposite of total trend
      if (i < 2) {
        // For first couple positions, sometimes force opposite direction for realism
        const forceOpposite = hash % 3 === 0 // 33% chance
        if (forceOpposite) {
          if (totalPnL > 0 && individualPnL > 0) {
            individualPnL = -Math.abs(individualPnL) * 0.5 // Make it negative but smaller
          } else if (totalPnL < 0 && individualPnL < 0) {
            individualPnL = Math.abs(individualPnL) * 0.7 // Make it positive
          }
        }
      }

      // Don't let any single position be more than 70% of total absolute value
      const maxIndividual = Math.abs(totalPnL) * 0.7
      individualPnL = Math.max(-maxIndividual, Math.min(maxIndividual, individualPnL))

      individualPnLs.push(Math.round(individualPnL))
      runningSum += individualPnL
    }

    // Last outcome gets the remainder to ensure exact sum
    individualPnLs.push(totalPnL - runningSum)

    // Ensure we have at least one positive and one negative (for realism)
    if (heldOutcomesList.length >= 2) {
      const hasPositive = individualPnLs.some((pnl) => pnl > 0)
      const hasNegative = individualPnLs.some((pnl) => pnl < 0)

      if (!hasPositive && totalPnL !== 0) {
        // Force at least one position to be positive
        const targetIndex = Math.floor(individualPnLs.length / 2)
        const adjustment = Math.min(Math.abs(totalPnL) * 0.3, 15000)
        individualPnLs[targetIndex] = adjustment
        // Adjust the last position to maintain the sum
        individualPnLs[individualPnLs.length - 1] =
          totalPnL - individualPnLs.slice(0, -1).reduce((sum, val) => sum + val, 0)
      }

      if (!hasNegative && totalPnL !== 0) {
        // Force at least one position to be negative
        const targetIndex = Math.floor(individualPnLs.length * 0.7)
        const adjustment = -Math.min(Math.abs(totalPnL) * 0.2, 10000)
        individualPnLs[targetIndex] = adjustment
        // Adjust the last position to maintain the sum
        individualPnLs[individualPnLs.length - 1] =
          totalPnL - individualPnLs.slice(0, -1).reduce((sum, val) => sum + val, 0)
      }
    }

    // Map the P&L values to outcomes
    heldOutcomesList.forEach((outcome, index) => {
      const pnlValue = individualPnLs[index]
      if (pnlValue !== undefined) {
        pnlMap.set(outcome.slug, pnlValue)
      }
    })

    return pnlMap
  }, [heldOutcomes, outcomes, simulatedPnL, title])

  const getOutcomePnL = (outcome: Outcome): number => {
    return outcomesPnLMap.get(outcome.slug) || 0
  }

  // Memoize eliminated outcome slugs to prevent creating new array on every render (unused but kept for future use)
  // const eliminatedOutcomeSlugs = useMemo(() => eliminatedOutcomes.map((outcome) => outcome.slug), [eliminatedOutcomes])

  useEffect(() => {
    if (onPnLChange && heldOutcomes.length > 0) {
      onPnLChange(title, simulatedPnL)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, simulatedPnL, heldOutcomes.length]) // onPnLChange is intentionally omitted to prevent infinite loops

  // -----------------------
  // Simulated trading hook (used for landing-page demo brackets)
  // -----------------------
  // Memoize dominant configs so the reference remains stable between renders.
  const memoDominantConfigs = useMemo(
    () => [
      { slug: "philadelphia-eagles", min: 10, max: 20 },
      { slug: "kansas-city-chiefs", min: 10, max: 20 },
    ],
    [],
  )

  useSimulatedTrading({
    enabled: simulate && isExpanded && state === "live" && !isFinal,
    heldOutcomesLength: heldOutcomes.length,
    title,
    setOutcomes,
    setRecentTrades,
    dominantConfigs: memoDominantConfigs,
  })

  return (
    <div className="relative">
      {/* Main card container */}
      <div className={`relative ${!isExpanded ? "group" : ""}`}>
        {/* Card thumbnail/Cover image */}
        <BracketCover
          imageUrl={imageUrl}
          heldOutcomes={heldOutcomes}
          isAnyCardExpanded={isAnyCardExpanded}
          isExpanded={isExpanded}
        />

        {/* Main interactive bracket card */}
        {/* Fade card image when other cards are expanded but this one isn't */}
        <button
          className={`bracket-card ${isExpanded ? "expanded bg-white/6" : "bg-white/6 active:bg-white/9"} transition-all backdrop-blur-[20px] rounded-[.9rem] py-3 px-4 min-h-16 text-white duration-100 cursor-pointer w-full text-left border border-white/3 relative overflow-hidden touch-manipulation select-none ${isAnyCardExpanded && !isExpanded ? "opacity-85 group-hover:opacity-100 [&>*:not(.bracket-title)]:opacity-85 [&>*:not(.bracket-title)]:group-hover:opacity-100" : ""}`}
          onClick={handleToggleExpand}
          onKeyUp={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleToggleExpand()
            }
          }}
          type="button"
        >
          {/* Live dot/state indicator - positioned in top right corner of card */}
          <AnimatePresence>
            {!isExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
                className="absolute top-[0.885rem] right-[1.04rem] flex items-center z-30"
              >
                {state === "live" && (
                  <div className="relative">
                    <div className="w-[0.425rem] h-[0.425rem] rounded-full bg-[#22FF65]" />
                  </div>
                )}
                {state === "final" && (
                  <div className="relative">
                    <CheckCheck className="-mr-[.2rem] -mt-1 w-[0.9rem] h-[0.9rem] text-white/60" />
                  </div>
                )}
                {state === "upcoming" && (
                  <div className="relative">
                    <Clock className="w-[0.9rem] h-[0.9rem] text-cyan-400" />
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Holding count chip - positioned consistently regardless of card state */}
          {heldOutcomes.length > 0 && state !== "final" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isExpanded ? 1 : 1 }}
              transition={{ duration: 0.2 }}
              className="absolute top-[0.52rem] right-[2.05rem] z-30"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-white/10 skew-x-[-16deg] rounded-[.25rem]" />
                <span className="relative text-white/70 text-[12px] font-semibold leading-none select-none px-[.38rem] py-1 block">
                  {heldOutcomes.length}
                </span>
              </div>
            </motion.div>
          )}

          {/* Progress bar at the top of card */}
          <div className="absolute left-0 top-0 right-0 h-16 bg-white/0 z-0">
            <div
              className="h-full bg-white/6 origin-left transform-gpu"
              style={{
                width: isFinal ? "100%" : `${progress}%`,
                clipPath: isFinal
                  ? "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
                  : "polygon(0 0, 100% 0, calc(100% - 16px) 100%, 0 100%)",
                animation: isFinal ? "none" : "topMeterSurge 2s ease-in-out infinite",
                borderRadius: isFinal ? "0 .9rem 0 0" : "none",
              }}
            />
          </div>

          {/* Card header with title and expand controls */}
          <div className="flex items-center justify-between w-full h-10 relative z-20">
            {/* Title section with icon */}
            <div
              className={`flex items-start gap-2 text-left grow min-w-0 ${imageUrl ? "pl-[3.55rem]" : "pl-[1.11rem]"}`}
            >
              {icon && <span className={`shrink-0 mt-[0.35rem] ${!imageUrl ? "ml-[0.2rem]" : ""}`}>{icon}</span>}
              {/* Title text */}
              <div className="flex items-center h-10 grow max-w-[calc(100%-1rem]">
                <span
                  ref={titleRef}
                  className={`bracket-title text-[1rem] leading-tight font-medium line-clamp-2 break-normal hyphens-none ${!imageUrl ? "-ml-2" : ""}`}
                >
                  {title ?? "%"}
                </span>
              </div>
            </div>

            {/* Right side of header with stats and chevron */}
            <div className="flex flex-col items-end gap-1 min-w-[3.9rem] relative h-10">
              {/* P&L display - positioned to left of chevron (same position in all states) */}
              {heldOutcomes.length > 0 && state !== "final" && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.2 }}
                  className="absolute bottom-[-0.33rem] right-4 text-right"
                >
                  <span
                    className={`text-[0.875rem] leading-none ${simulatedPnL >= 0 ? "text-[#22FF65]" : "text-[#FF2D30]"}`}
                  >
                    {formatPnL(simulatedPnL)}
                  </span>
                </motion.div>
              )}

              {/* Expand/collapse chevron - fixed position in header area */}
              <motion.div
                animate={{ rotate: isExpanded ? 180 : 0 }}
                transition={{ duration: 0.2 }}
                className="absolute bottom-[-0.12rem] -right-1 shrink-0 z-30"
              >
                <ChevronDownIcon className="w-[.9375rem] h-[.9375rem] text-white/50" />
              </motion.div>
            </div>
          </div>

          {/* Expandable content section */}
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="relative mt-[.7rem] pt-4 border-t border-white/5">
                  {/*
                    Search Outcomes:
                    - Clicking WINNER/POSITIONS toggles the filter input
                    - In positions mode, only held outcomes are shown
                    - Users can search for specific outcomes by name
                    - This helps users quickly find teams in brackets with many outcomes
                    - Future enhancement: add filtering by seed, odds, etc.
                  */}
                  <div className="flex justify-between mb-4 mt-0.5 relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowFilter(!showFilter)
                        if (!showFilter) {
                          setFilterValue("")
                        }
                      }}
                      className={`text-[11.75px] ml-[1.1rem] tracking-[.055rem] transition-colors flex items-center gap-1.5 ${showFilter ? "text-white" : "text-white/70 hover:text-white/90"}`}
                      type="button"
                    >
                      <span>{showOnlyHeldOutcomes ? "POSITION" : "WINNER"}</span>
                      <ListFilter className="w-3 h-3 -translate-x-[0.15rem] opacity-70" aria-hidden="true" />
                    </button>
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center">
                      {state === "final" ? (
                        <>
                          <div className="relative mr-1">
                            <CheckCheck className="w-[0.9rem] h-[0.9rem] text-white/70" />
                          </div>
                          <span className="text-white/70 text-[0.735rem] tracking-[.025rem]">FINAL</span>
                        </>
                      ) : state === "live" ? (
                        <>
                          {/* Live indicator */}
                          <div className="relative flex items-center justify-center px-3 py-0.5 rounded-full border border-white/10">
                            <div className="relative mr-1.5">
                              <div className="w-[0.425rem] h-[0.425rem] rounded-full bg-[#22FF65]" />
                              <div className="absolute inset-0 w-[0.425rem] h-[0.425rem] rounded-full bg-[#22FF65] animate-ping opacity-75" />
                            </div>
                            <span className="text-[#22FF65] text-[0.735rem] tracking-[.025rem]">
                              {simulate ? "WARM-UP" : "LIVE"}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="relative mr-1.5">
                            <Clock className="w-[0.9rem] h-[0.9rem] text-cyan-400" />
                          </div>
                          <span className="text-cyan-400 text-[0.735rem] tracking-[.025rem]">UPCOMING</span>
                        </>
                      )}
                    </div>
                    <button
                      className="text-white/70 text-xs mr-4 tracking-[.055rem] cursor-pointer hover:text-white/90 transition-colors flex items-center gap-1"
                      onClick={toggleDisplayMode}
                      type="button"
                    >
                      <span className={`inline-block ${displayMode === "price" ? "transform -scale-y-100" : ""}`}>
                        <ArrowsUpDownIcon className="w-3 h-3 opacity-70" />
                      </span>
                      <span className="text-[0.735rem] mr-1.5">{displayMode === "chance" ? "ODDS" : "BRACKY"}</span>
                    </button>
                  </div>

                  {/* Filter input */}
                  {showFilter && (
                    <div className="overflow-hidden mb-4">
                      <div className="px-2">
                        <div className="relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">
                            <Search className="w-4 h-4" />
                          </div>
                          <input
                            ref={filterInputRef}
                            type="text"
                            value={filterValue}
                            onChange={(e) => setFilterValue(e.target.value)}
                            placeholder="Search..."
                            className="w-full bg-white/4 border border-white/10 rounded-md pl-[2.2rem] pr-32 py-2 text-sm text-white placeholder-white/40 focus:outline-hidden focus:border-white/20 transition-colors"
                            onClick={(e) => e.stopPropagation()}
                          />
                          {filterValue && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setFilterValue("")
                              }}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                              type="button"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Active outcomes */}
                  <div className="space-y-3">
                    {activeOutcomes.map((outcome, index) => (
                      <div key={outcome.name} className="space-y-1">
                        <BracketCardOutcomeRow
                          outcome={outcome}
                          index={index}
                          isSelected={selectedOutcomes.includes(outcome.name)}
                          isExpanded={expandedOutcomes.has(outcome.name)}
                          showLogos={showLogos}
                          displayMode={displayMode}
                          state={state}
                          isFinal={isFinal}
                          heldOutcomes={heldOutcomes}
                          recentTrades={recentTrades}
                          formatPnL={formatPnL}
                          getOutcomePnL={getOutcomePnL}
                          onOutcomeClick={handleOutcomeClick}
                          selectedOutcomes={selectedOutcomes}
                        />

                        {/* Expanded stats section */}
                        <BracketCardOutcomeStats
                          isExpanded={expandedOutcomes.has(outcome.name)}
                          outcomeName={outcome.name}
                          heldOutcomes={heldOutcomes}
                          outcomeSlug={outcome.slug}
                        />
                      </div>
                    ))}

                    {/* Divider between active and eliminated teams */}
                    {showEliminatedDivider && (
                      <div className="mt-[.85rem]! mb-[.8rem]!">
                        <EliminatedDivider
                          eliminatedCount={eliminatedOutcomes.length}
                          showEliminated={showEliminated}
                          onToggleEliminated={
                            eliminatedDividerInteractive ? () => setShowEliminated(!showEliminated) : undefined
                          }
                          interactive={eliminatedDividerInteractive}
                        />
                      </div>
                    )}

                    {/* Space beneath eliminated teams */}
                    {showEliminated &&
                      eliminatedOutcomes.map((outcome, index) => (
                        <div key={outcome.name} className={`mb-[.8rem]! ${index === 0 ? "-mt-1!" : ""}`}>
                          <BracketCardOutcomeRow
                            outcome={outcome}
                            index={activeOutcomes.length + index}
                            isSelected={selectedOutcomes.includes(outcome.name)}
                            isExpanded={expandedOutcomes.has(outcome.name)}
                            showLogos={showLogos}
                            displayMode={displayMode}
                            state={state}
                            isFinal={isFinal}
                            heldOutcomes={heldOutcomes}
                            recentTrades={recentTrades}
                            formatPnL={formatPnL}
                            getOutcomePnL={getOutcomePnL}
                            onOutcomeClick={handleOutcomeClick}
                            selectedOutcomes={selectedOutcomes}
                          />

                          {/* Expanded stats section */}
                          <BracketCardOutcomeStats
                            isExpanded={expandedOutcomes.has(outcome.name)}
                            outcomeName={outcome.name}
                            heldOutcomes={heldOutcomes}
                            outcomeSlug={outcome.slug}
                          />
                        </div>
                      ))}
                  </div>
                  {/* Footer with volume info */}
                  <div className="mb-2.5 pt-2 flex justify-center">
                    <div className="flex items-center gap-1 text-white/30 text-xs tracking-wide">
                      {simulate ? (
                        <span className="font-regular">SIMULATED TRADING</span>
                      ) : (
                        <>
                          <span className="font-regular">Total Volume:</span>
                          <span className="text-white/50 font-regular">${formatVolume(volume)} BRACKY</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>
    </div>
  )
})
